<template>
  <div>
    <div
      id="do-custom-attribute"
      class="flex h-[64vh] flex-col space-y-5 overflow-y-auto px-4 pt-1 2xl:h-[78vh]"
    >
      <div class="flex flex-col space-y-3">
        <lf-input
          :value="activeStep?.label || ''"
          class="h-10 w-full"
          name="block_name"
          data-cy="block-name-input"
          :placeholder="$t('ORCHESTRATION.BLOCK_NAME')"
          no-margin
          no-padding
          immediate
          :disabled="isActiveTemplatePublished"
          @key-released="handleLabelChange"
        />
        <search-input
          v-model="searchPhrase"
          data-cy="dsd-search-input"
          class="h-10 w-full"
          no-padding
          no-margin
          :delay="100"
          :disabled="isActiveTemplatePublished"
        />
      </div>
      <button
        class="space-x-1 text-right font-semibold text-primary"
        :disabled="isActiveTemplatePublished"
        @click="showCreateAttributeModal = true"
      >
        <span class="text-lg">+</span>
        <span>{{ $t("ORCHESTRATION.CREATE_NEW_ATTRIBUTE") }}</span>
      </button>
      <div
        class="flex flex-col space-y-2 overflow-auto rounded-sm bg-white p-2"
      >
        <no-data-found v-if="!filteredItems.length" />
        <lf-checkbox
          v-if="filteredItems.length"
          name="attributes-select-all"
          value="select-all"
          class="flex items-center space-x-2 p-1"
          label-class="text-gray-400"
          :model-value="!!areAllFilteredSelected"
          @update:model-value="areAllFilteredSelected = $event"
        >
          {{ $t("COMMON.SELECT_ALL") }}
        </lf-checkbox>
        <custom-attributes-list
          :search-phrase="searchPhrase"
          :attributes="options"
          :filtered-attributes="filteredItems"
          :selected="selected"
          @update:selected="handleSave"
        />
      </div>
      <webhook-configuration
        v-if="activeStep?.id"
        :webhooks="activeStep.webhooks"
        :step-id="activeStep.id"
      />
    </div>
    <modal-buttons
      v-if="!isActiveTemplatePublished"
      :on-delete="handleDelete"
      class="bg-disabled p-6"
      :white-background="false"
    />
    <custom-attributes-modal
      v-if="showCreateAttributeModal"
      @close="showCreateAttributeModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useFuse } from "@vueuse/integrations/useFuse";
import { computed, ref } from "vue";
import { useStore } from "vuex";

import {
  FUZZY_SEARCH_THRESHOLD_DEFAULT,
  SERVICE_CUSTOM,
} from "@/helpers/constants";
import { useDoActiveTemplateStatus } from "@/hooks/orchestration";

import CustomAttributesModal from "@/components/customAttributesV2/CustomAttributesModal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import WebhookConfiguration from "@/views/profile/components/dataOrchestration/WebhookConfiguration.vue";
import NoDataFound from "@/views/profile/components/dataOrchestration/configPanel/NoDataFound.vue";
import CustomAttributesList from "@/views/profile/components/dataOrchestration/configPanel/customAttributes/CustomAttributesList.vue";

import type {
  IAttribute,
  ICustomAttributeContext,
  IStep,
  IStepAttribute,
} from "@/models/orchestration";

const { getters, commit } = useStore();
const { isActiveTemplatePublished } = useDoActiveTemplateStatus();

const searchPhrase = ref("");

const showCreateAttributeModal = ref(false);

const activeStep = computed<IStep | null>(
  () => getters["orchestration/activeStep"]
);

const attributes = computed<IAttribute[]>(
  () => getters["orchestration/customAttributesAttributes"]
);

const areAllFilteredSelected = computed({
  get() {
    return (
      !!filteredItems.value.length &&
      filteredItems.value.every((attribute) =>
        selectedAttributesIds.value.includes(attribute.id)
      )
    );
  },
  set(value) {
    const currentSelection = [...selected.value];
    const filteredIds = filteredItems.value.map((attribute) => attribute.id);

    if (value) {
      // Add all filtered items that aren't already selected
      filteredIds.forEach((id) => {
        if (!currentSelection.some((attr) => attr.id === id)) {
          const attributeToAdd = options.value.find((attr) => attr.id === id);
          if (attributeToAdd) {
            currentSelection.push({ id, context: attributeToAdd.context });
          }
        }
      });
    } else {
      // Remove all filtered items from selection
      filteredIds.forEach((id) => {
        const index = currentSelection.findIndex((attr) => attr.id === id);
        if (index > -1) {
          currentSelection.splice(index, 1);
        }
      });
    }

    handleSave(currentSelection);
  },
});

// attributes with context.value.id assigned as id
const options = computed<IAttribute[]>(() => {
  return attributes.value
    .map((attribute) => ({
      ...attribute,
      id: (attribute.context?.value as ICustomAttributeContext).id,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

// saved IStepAttributes with context.value.id assigned as id
const selected = computed<IStepAttribute[]>(() => {
  const savedAttributes = activeStep.value?.attributes || [];

  return options.value.reduce<IStepAttribute[]>((acc, option) => {
    if (
      savedAttributes.some(
        (savedAttribute) =>
          (savedAttribute.context?.value as ICustomAttributeContext).id ===
          option.id
      )
    ) {
      acc.push({ id: option.id, context: option.context });
    }
    return acc;
  }, []);
});

const selectedAttributesIds = computed(() =>
  selected.value.map((attr) => attr.id)
);

const { results } = useFuse(searchPhrase, options, {
  fuseOptions: {
    keys: ["name"],
    threshold: FUZZY_SEARCH_THRESHOLD_DEFAULT,
  },
  matchAllWhenSearchEmpty: true,
});

const filteredItems = computed(() => results.value.map(({ item }) => item));

const handleSave = (updatedSelection: IStepAttribute[] = []) => {
  const removedAttributes = selected.value?.reduce<IStepAttribute[]>(
    (acc, select) => {
      if (!updatedSelection.some(({ id }) => id === select.id)) {
        // setting the original Custom Attribute id = "custom"
        acc.push({ ...select, id: SERVICE_CUSTOM });
      }
      return acc;
    },
    []
  );

  const updatedAttributes = attributes.value.reduce<IStepAttribute[]>(
    (acc, attr) => {
      if (
        updatedSelection.some(
          ({ id }) => id === (attr.context?.value as ICustomAttributeContext).id
        )
      ) {
        acc.push({ id: attr.id, context: attr.context });
      }
      return acc;
    },
    []
  );

  commit("orchestration/setStepAttributes", {
    stepId: activeStep.value?.id,
    attributes: updatedAttributes,
  });

  if (removedAttributes.length) {
    commit("orchestration/removeAttributesFromOutcomes", {
      stepId: activeStep.value?.id,
      removedAttributes,
    });
  }
};

const handleLabelChange = (value: string) => {
  commit("orchestration/setStepLabel", {
    stepId: activeStep.value?.id,
    label: value,
  });
};

const handleDelete = () => {
  commit("orchestration/deleteStep", { stepId: activeStep.value?.id });
  commit("orchestration/setActiveConfigPanel", "");
  commit("orchestration/setActiveStep", {});
};
</script>
